// vim: set ts=2 sts=2 sw=2 et:
//
// This file is part of OpenLifter, simple Powerlifting meet software.
// Copyright (C) 2019 The OpenPowerlifting Project.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as
// published by the Free Software Foundation, either version 3 of the
// License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.

// WRPF Pesaje: search box + weigh-in/presence filters shown above the Weigh-ins tables.

import React from "react";

import Button from "react-bootstrap/Button";
import FormControl from "react-bootstrap/FormControl";

import { getString } from "../../logic/strings";
import {
  hasActiveWeighinsFilters,
  PresenceFilter,
  WeighInFilter,
  WeighinsFilters as WeighinsFiltersState,
} from "../../logic/weighins/filterWeighins";
import { Language } from "../../types/dataTypes";

interface Props {
  filters: WeighinsFiltersState;
  language: Language;
  visibleCount: number;
  totalCount: number;
  onChange: (filters: WeighinsFiltersState) => void;
  onClear: () => void;
}

const WeighinsFilters: React.FC<Props> = (props) => {
  const { filters, language } = props;

  const handleQuery = (event: React.BaseSyntheticEvent) => {
    const value = event.currentTarget.value;
    if (typeof value === "string") props.onChange({ ...filters, query: value });
  };

  const handleWeighIn = (event: React.BaseSyntheticEvent) => {
    const value = event.currentTarget.value;
    if (value === "all" || value === "pending" || value === "done") {
      props.onChange({ ...filters, weighIn: value as WeighInFilter });
    }
  };

  const handlePresence = (event: React.BaseSyntheticEvent) => {
    const value = event.currentTarget.value;
    if (value === "all" || value === "present" || value === "absent") {
      props.onChange({ ...filters, presence: value as PresenceFilter });
    }
  };

  const count = getString("weigh-ins.filter-count", language)
    .replace("{visible}", String(props.visibleCount))
    .replace("{total}", String(props.totalCount));

  return (
    <div className="d-flex flex-wrap align-items-center" style={{ gap: "8px", marginBottom: "17px" }}>
      <FormControl
        type="search"
        aria-label={getString("weigh-ins.search-placeholder", language)}
        placeholder={getString("weigh-ins.search-placeholder", language)}
        value={filters.query}
        onChange={handleQuery}
        style={{ maxWidth: "280px" }}
      />
      <FormControl
        as="select"
        className="custom-select"
        aria-label={getString("weigh-ins.weighed-in-header", language)}
        value={filters.weighIn}
        onChange={handleWeighIn}
        style={{ maxWidth: "200px" }}
      >
        <option value="all">{getString("weigh-ins.filter-weigh-in-all", language)}</option>
        <option value="pending">{getString("weigh-ins.filter-weigh-in-pending", language)}</option>
        <option value="done">{getString("weigh-ins.filter-weigh-in-done", language)}</option>
      </FormControl>
      <FormControl
        as="select"
        className="custom-select"
        aria-label={getString("weigh-ins.present-header", language)}
        value={filters.presence}
        onChange={handlePresence}
        style={{ maxWidth: "200px" }}
      >
        <option value="all">{getString("weigh-ins.filter-presence-all", language)}</option>
        <option value="present">{getString("weigh-ins.filter-presence-present", language)}</option>
        <option value="absent">{getString("weigh-ins.filter-presence-absent", language)}</option>
      </FormControl>
      <span className="text-muted">{count}</span>
      <Button variant="outline-secondary" onClick={props.onClear} disabled={!hasActiveWeighinsFilters(filters)}>
        {getString("weigh-ins.filter-clear", language)}
      </Button>
    </div>
  );
};

export default WeighinsFilters;
